import React, { memo, useCallback, useMemo, useState } from 'react';

import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction, useTranslation } from '@lyearn/i18n';
import { Trans } from '@lyearn/i18n';
import { EditOutline, Trash2Outline } from '@lyearn/icons';
import { ActionMenu, ActionMenuItemType } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Avatar, Box, Dot, Stack, Typography } from '@lyearn/ui';
import useLinking from '@/hooks/useLinking';
import * as AdminEntity from '@/modules/corporate/modules/admin/components/AdminEntity';
import {
  CreateTaskCreationConfigInput,
  OkrTaskCreationConfig,
  ProviderType,
  SyncStatusEnum,
} from '@/types/schema';

import { AutoSyncActions } from './types';

type ListItemProps = {
  taskCreationConfig: OkrTaskCreationConfig;
  isFirstItem?: boolean;
  setInViewJQLQueryDialog: React.Dispatch<
    React.SetStateAction<OkrTaskCreationConfig | CreateTaskCreationConfigInput | undefined>
  >;
  onDeleteJQLQuery: (taskCreationConfigId: string) => Promise<void>;
  provider: ProviderType;
};

const OVERRIDES = {
  PopoverButton: {
    classes: {
      open: 'opacity-100',
      closed: 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100',
    },
  },
};

const getMenuItems = ({
  t,
  confirmDelete,
  provider,
  hyperLink,
}: {
  t: TFunction;
  confirmDelete: boolean;
  provider: ProviderType;
  hyperLink?: string | null;
}) =>
  getEnabledItems([
    {
      id: AutoSyncActions.Edit,
      label: t('Common:Actions.Edit'),
      EndIcon: EditOutline,
      enabled: true,
    },
    {
      id: AutoSyncActions.Delete,
      label: t('Common:Actions.Delete'),
      EndIcon: Trash2Outline,
      appearance: 'danger',
      enabled: !confirmDelete,
      keepOpen: true,
    },
    {
      id: AutoSyncActions.DeleteForSure,
      label: t('Common:Actions.Sure'),
      EndIcon: Trash2Outline,
      appearance: 'danger',
      enabled: confirmDelete,
    },
    {
      id: 'divider',
      type: 'divider',
      enabled: !!hyperLink,
    },
    {
      id: AutoSyncActions.OpenInProvider,
      label: t('Jira:OpenInProvider', { providerName: provider?.name?.toUpperCase() ?? '' }),
      endIcon: <Avatar name="Avatar" size="x-small" src={provider?.logo?.url ?? ''} />,
      enabled: !!hyperLink,
      appearance: 'link',
    },
  ]);

function ListItem(props: ListItemProps) {
  const { t } = useTranslation();
  const { taskCreationConfig, isFirstItem, setInViewJQLQueryDialog, onDeleteJQLQuery, provider } =
    props;
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { openLink } = useLinking();

  const onAction = useCallback(
    async (actionType: AutoSyncActions) => {
      switch (actionType) {
        case AutoSyncActions.Delete:
          setConfirmDelete(true);
          break;
        case AutoSyncActions.DeleteForSure:
          onDeleteJQLQuery(taskCreationConfig.id);
          break;
        case AutoSyncActions.Edit:
          setInViewJQLQueryDialog(taskCreationConfig);
          break;
        case AutoSyncActions.OpenInProvider:
          openLink(taskCreationConfig.externalLink ?? '');
          break;
        default:
      }
    },
    [onDeleteJQLQuery, openLink, setInViewJQLQueryDialog, taskCreationConfig],
  );

  const onToggleMenu = useCallback((open: boolean) => {
    if (open) {
      setConfirmDelete(false);
    }
  }, []);

  const menuItems = useMemo(
    () =>
      getMenuItems({
        t,
        confirmDelete,
        hyperLink: taskCreationConfig.externalLink,
        provider,
      }) as ActionMenuItemType[],
    [t, confirmDelete, taskCreationConfig.externalLink, provider],
  );

  return (
    <Stack alignItems="center" className="group rounded-8 px-8" role="button">
      <Stack
        alignItems="center"
        className={cn('w-full py-16 px-8', {
          'border-t border-border ': !isFirstItem,
        })}
        justifyContent="between">
        <Stack className="truncate pe-20" direction="col">
          <Typography className="truncate" color="text-text-paragraph" variant="body-short-01">
            {taskCreationConfig.searchText}
          </Typography>
          <Typography
            className="mt-4 flex items-center"
            color="text-text-secondary"
            variant="body-short-02">
            {/*taskCreationConfig.syncConfig is null for backward compatibility*/}
            {/*syncConfig.syncStatus is completed or syncConfig is null that means issues are added and need to show issue count*/}
            {!taskCreationConfig.syncConfig ||
            taskCreationConfig.syncConfig?.syncStatus === SyncStatusEnum.Completed ? (
              <Trans
                color="text-text-secondary"
                components={{
                  space: <Typography className="mx-2" variant="label-02" />,
                  dot: <Dot />,
                }}
                i18nKey="Jira:Tasks.TaskConfigDetails"
                values={{
                  count: taskCreationConfig.actionItemsLinkedCount,
                  userName: taskCreationConfig.createdByUser?.name,
                }}
              />
            ) : (
              t('Jira:LoadingIssues')
            )}
          </Typography>
        </Stack>
        <Box className="flex h-40 items-center ms-4" data-testid="action-menu-button">
          <ActionMenu<AutoSyncActions>
            items={menuItems}
            overrides={OVERRIDES}
            onAction={onAction}
            onOpenChange={onToggleMenu}>
            <AdminEntity.ActionButton />
          </ActionMenu>
        </Box>
      </Stack>
    </Stack>
  );
}

export default memo(ListItem);
